import { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Global flag to pause background color sync (used by video camera overlay)
let backgroundSyncPaused = false;
// Store the current theme globally so resumeBackgroundSync can restore the correct color
// Initialize from localStorage if available
let currentGlobalTheme: Theme = (() => {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('gitfit-theme');
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  }
  return 'dark';
})();

export const pauseBackgroundSync = () => {
  backgroundSyncPaused = true;
  console.log('[ThemeProvider] Background sync paused');
};

export const resumeBackgroundSync = () => {
  backgroundSyncPaused = false;
  console.log('[ThemeProvider] Background sync resumed');
  
  // Immediately restore the correct background color based on current theme
  if (typeof window !== 'undefined' && window.natively?.setAppBackgroundColor) {
    const bgColor = currentGlobalTheme === 'dark' ? '#09090b' : currentGlobalTheme === 'system' ? '#1a1a1a' : '#ffffff';
    window.natively.setAppBackgroundColor(bgColor);
    console.log('[ThemeProvider] Immediately restored background color:', bgColor);
  }
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Check localStorage first
    const stored = localStorage.getItem('gitfit-theme');
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
    // Default to dark theme
    return 'dark';
  });

  const intervalIdRef = useRef<number | null>(null);

  // Function to apply only the background color (for periodic cleanup)
  const applyBackgroundColorOnly = (currentTheme: Theme) => {
    // Skip if sync is paused (e.g., video camera is open)
    if (backgroundSyncPaused) return;
    
    if (typeof window !== 'undefined' && window.natively?.setAppBackgroundColor) {
      const bgColor = currentTheme === 'dark' ? '#09090b' : currentTheme === 'system' ? '#1a1a1a' : '#ffffff';
      window.natively.setAppBackgroundColor(bgColor);
    }
  };

  useEffect(() => {
    const root = document.documentElement;
    
    // Update global theme reference for resumeBackgroundSync
    currentGlobalTheme = theme;
    
    // Remove all theme classes first
    root.classList.remove('light', 'dark', 'system');
    
    // Add the current theme class
    root.classList.add(theme);
    
    // Save to localStorage
    localStorage.setItem('gitfit-theme', theme);

    // Update native app background and status bar colors (full setup on theme change)
    if (typeof window !== 'undefined' && window.natively) {
      if (theme === 'dark') {
        // Dark theme: light status bar text + dark background
        const darkBgColor = '#09090b'; // Matches --background: 240 10% 3.9%
        if (window.natively.setAppBackgroundColor) {
          window.natively.setAppBackgroundColor(darkBgColor);
        }
        if (window.natively.setAppStatusBarStyleIOS) {
          window.natively.setAppStatusBarStyleIOS('LIGHT');
        }
      } else if (theme === 'system') {
        // System theme: light status bar text + dark grey background
        const systemBgColor = '#1a1a1a'; // Matches --background: 0 0% 10%
        if (window.natively.setAppBackgroundColor) {
          window.natively.setAppBackgroundColor(systemBgColor);
        }
        if (window.natively.setAppStatusBarStyleIOS) {
          window.natively.setAppStatusBarStyleIOS('LIGHT');
        }
      } else {
        // Light theme: dark status bar text + light background
        const lightBgColor = '#ffffff'; // Matches --background: 0 0% 100%
        if (window.natively.setAppBackgroundColor) {
          window.natively.setAppBackgroundColor(lightBgColor);
        }
        if (window.natively.setAppStatusBarStyleIOS) {
          window.natively.setAppStatusBarStyleIOS('DARK');
        }
      }
    }
  }, [theme]);

  // Periodically re-apply background color to fix any external interference
  useEffect(() => {
    // Only run in native app environment
    if (typeof window !== 'undefined' && window.natively?.setAppBackgroundColor) {
      // Set up periodic cleanup (every 1 second) - only background color
      intervalIdRef.current = window.setInterval(() => {
        applyBackgroundColorOnly(theme);
      }, 1000);

      console.log('[ThemeProvider] Started periodic background color sync (1s interval)');

      // Cleanup on unmount
      return () => {
        if (intervalIdRef.current !== null) {
          window.clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
          console.log('[ThemeProvider] Stopped periodic background color sync');
        }
      };
    }
  }, [theme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  const toggleTheme = () => {
    setThemeState((prev) => {
      if (prev === 'light') return 'system';
      if (prev === 'system') return 'dark';
      return 'light';
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
